import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Truck, Home as HomeIcon, ArrowLeft, ArrowRight, Plus, Package, Flag,
  Loader2, Camera, MessageCircle, CheckCircle2, AlertTriangle, X, Chrome,
} from "lucide-react";
import { api, brl, API } from "../lib/api";
import { processarFoto, logErroUpload } from "../lib/image";
import Header from "../components/Header";
import { Calendar } from "../components/ui/calendar";
import AddressForm from "../components/wizard/AddressForm";
import ClienteGate from "../components/wizard/ClienteGate";
import ServicosAdicionaisStep from "../components/wizard/ServicosAdicionaisStep";
import RoutePreview from "../components/wizard/RoutePreview";
import ItemSelector from "../components/wizard/ItemSelector";
import CaronaCard from "../components/wizard/CaronaCard";
import Counter from "../components/wizard/Counter";
import { PaymentStep, ContractStep, SinalStep, FinalStep } from "../components/wizard/PaymentSteps";

const emptyAddr = () => ({
  cep: "", numero: "", complemento: "", referencia: "", logradouro: "", bairro: "", cidade: "", uf: "", observacao: "",
});

const STEP_TITLES = [
  "Tipo de serviço", "Endereços", "Itens", "Serviços adicionais", "Acesso ao local", "Escadas e elevador",
  "Ajudantes", "Carona", "Serviços adicionais", "Fotos", "Seus dados", "Seu orçamento",
  "Pagamento", "Contrato", "Sinal", "Reserva",
];
const TOTAL_STEPS = STEP_TITLES.length;

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#F5B301] focus:border-transparent placeholder:text-slate-400";

function OptionGrid({ options, value, onChange, testIdPrefix }) {
  return (
    <div className="grid grid-cols-2 gap-2.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          data-testid={`${testIdPrefix}-${opt.value}`}
          onClick={() => onChange(opt.value)}
          className={`py-3.5 px-3 rounded-xl border-2 text-sm font-semibold active:scale-95 transition-all ${
            value === opt.value
              ? "border-[#F5B301] bg-gold-light text-navy"
              : "border-slate-200 bg-white text-slate-600"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default function QuoteWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [routeInfo, setRouteInfo] = useState(null);
  const [calcRoute, setCalcRoute] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [publicSettings, setPublicSettings] = useState(null);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [occupiedDates, setOccupiedDates] = useState([]);
  const [payment, setPayment] = useState({
    forma: "", cardTipo: "", bandeira: "", parcelas: 1, cpfCnpj: "", calc: null,
  });
  const [reservation, setReservation] = useState(null);
  const [googleUser, setGoogleUser] = useState(null);
  const [editQuoteId, setEditQuoteId] = useState(null);
  const [clientToken, setClientToken] = useState(() => localStorage.getItem("client_token") || "");
  const restauradoRef = useRef(false);
  const [servicosCfg, setServicosCfg] = useState(null);

  const [form, setForm] = useState({
    tipo: ["frete", "mudanca", "entrega"].includes(searchParams.get("tipo")) ? searchParams.get("tipo") : "",
    origin: emptyAddr(),
    destination: emptyAddr(),
    stops: [],
    items: {},
    outrosItens: "",
    acesso: { nivel: "", caminhaoPara: "", distancia: "" },
    escada: { existe: null, lances: 1, degraus: 0 },
    elevador: { existe: null, comporta: "" },
    ajudantes: { precisa: null, quantidade: 1 },
    carona: null,
    servicos: { embalagem: false, desmontagem: false, montagem: false },
    servicosAdicionais: { itens: {}, pacote_id: "" },
    servicosFotos: [],
    fotos: [],
    agendamento: { data: "", horario: "" },
    urgente: false,
    cliente: { nome: "", telefone: "", email: "", cpf: "" },
  });

  useEffect(() => {
    api.get("/public/settings").then((r) => setPublicSettings(r.data)).catch(() => {});
    api.get("/agenda/reservadas").then((r) => setOccupiedDates(r.data.dates || [])).catch(() => {});
    api.get("/public/servicos-adicionais").then((r) => setServicosCfg(r.data)).catch(() => {});
    let restaurado = false;
    try {
      const saved = JSON.parse(sessionStorage.getItem("cjr_wizard_state") || "null");
      if (saved && saved.form && saved.ts && Date.now() - saved.ts < 30 * 60 * 1000) {
        setForm(saved.form);
        if (typeof saved.step === "number") setStep(saved.step);
        if (saved.payment) setPayment(saved.payment);
        setPhotoPreviews((saved.form.fotos || []).map((p) => ({ path: p, preview: "" })));
        restaurado = true;
        restauradoRef.current = true;
      }
      sessionStorage.removeItem("cjr_wizard_state");
    } catch {}
    if (!restaurado && searchParams.get("tipo")) setStep(1);
    const qid = searchParams.get("editar") || searchParams.get("continuar");
    if (qid) {
      const t = localStorage.getItem("client_token");
      api.get(`/cliente/orcamentos/${qid}`, t ? { headers: { Authorization: `Bearer ${t}` } } : {})
        .then((r) => {
          const q = r.data.quote;
          setEditQuoteId(qid);
          setForm({
            tipo: q.tipo, origin: q.origin, destination: q.destination, stops: q.stops || [],
            items: q.items || {}, outrosItens: q.outros_itens || "",
            acesso: q.acesso || { nivel: "", caminhaoPara: "", distancia: "" },
            escada: q.escada || { existe: null, lances: 1, degraus: 0 },
            elevador: q.elevador || { existe: null, comporta: "" },
            ajudantes: q.ajudantes || { precisa: null, quantidade: 1 },
            carona: q.carona ?? null,
            servicos: q.servicos || { embalagem: false, desmontagem: false, montagem: false },
            servicosAdicionais: q.servicos_adicionais || { itens: {}, pacote_id: "" },
            servicosFotos: q.servicos_fotos || [],
            fotos: q.fotos || [],
            agendamento: q.agendamento || { data: "", horario: "" },
            urgente: !!q.urgente,
            cliente: { nome: q.cliente?.nome || "", telefone: q.cliente?.telefone || "", email: q.cliente?.email || "", cpf: q.cliente?.cpf || "" },
          });
          setPhotoPreviews((q.fotos || []).map((p) => ({ path: p, preview: "" })));
          setResult(r.data.client_view);
          setStep(searchParams.get("continuar") ? 11 : 1);
        })
        .catch((e) => {
          toast.error(e.response?.data?.detail || "Este orçamento não está mais disponível");
          navigate("/meus-servicos");
        });
    }
    api.get("/cliente/google/me").then((r) => {
      setGoogleUser(r.data);
      setForm((f) => ({ ...f, cliente: { ...f.cliente, nome: f.cliente.nome || r.data.nome, email: f.cliente.email || r.data.email } }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cliente já identificado (token salvo): preenche nome/telefone/CPF automaticamente
  // a partir do cadastro — evita pedir os dados de novo na etapa de agendamento.
  useEffect(() => {
    if (!clientToken) return;
    const nomePronto = !!form.cliente.nome?.trim();
    const telPronto = (form.cliente.telefone || "").replace(/\D/g, "").length >= 10;
    if (nomePronto && telPronto) return;
    api.get("/cliente/me", { headers: { Authorization: `Bearer ${clientToken}` } })
      .then((r) => {
        setForm((f) => ({
          ...f,
          cliente: {
            nome: f.cliente.nome || r.data.nome || "",
            telefone: f.cliente.telefone || r.data.telefone || "",
            cpf: f.cliente.cpf || r.data.cpf || "",
            email: f.cliente.email || r.data.email || "",
          },
        }));
      })
      .catch((e) => {
        if (e.response?.status === 401) {
          localStorage.removeItem("client_token");
          setClientToken("");
          toast.info("Sua sessão expirou — identifique-se novamente para continuar de onde parou.");
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientToken]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSub = (k, sub) => setForm((f) => ({ ...f, [k]: { ...f[k], ...sub } }));

  const setItems = (v) => {
    const total = Object.values(v).reduce((a, b) => a + b, 0);
    if (form.tipo === "frete" && total > 3) {
      setForm((f) => ({ ...f, tipo: "mudanca", items: v }));
      toast.warning("Pela quantidade de itens, seu serviço será classificado como MUDANÇA. Seus itens foram mantidos.", { duration: 5000 });
      return;
    }
    set("items", v);
  };

  const validAddr = (a) => a.cep.replace(/\D/g, "").length === 8 && a.numero.trim();

  const recalcRoute = async (origin, stops, destination) => {
    setCalcRoute(true);
    try {
      const r = await api.post("/route/calculate", { addresses: [origin, ...stops, ...[destination]] });
      setRouteInfo(r.data);
      (r.data.warnings || []).forEach((w) => toast.info(w, { duration: 6000 }));
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível calcular a rota. Verifique os endereços.");
      return null;
    } finally {
      setCalcRoute(false);
    }
  };

  const next = async () => {
    if (step === 0 && !form.tipo) return toast.error("Escolha FRETE ou MUDANÇA");
    if (step === 0 && form.tipo === "entrega" && publicSettings && !publicSettings.entrega_ativa) {
      set("tipo", "");
      return toast.error("O serviço de entrega está temporariamente indisponível");
    }
    if (step === 1) {
      if (!validAddr(form.origin)) return toast.error("Preencha CEP e número do local de embarque");
      if (!validAddr(form.destination)) return toast.error("Preencha CEP e número do destino final");
      const stopsOk = form.stops.every((s) => validAddr(s));
      if (!stopsOk) return toast.error("Complete CEP e número de todas as paradas");
      const info = await recalcRoute(form.origin, form.stops, form.destination);
      if (!info) return;
    }
    if (step === 2 && Object.keys(form.items).length === 0 && !form.outrosItens.trim()) {
      return toast.error("Selecione pelo menos um item ou descreva em outros itens");
    }
    if (step === 2 && form.tipo === "frete" && Object.values(form.items).reduce((a, b) => a + b, 0) > 3) {
      setForm((f) => ({ ...f, tipo: "mudanca" }));
      toast.warning("Pela quantidade de itens, seu serviço será classificado como MUDANÇA.", { duration: 5000 });
      return;
    }
    if (step === 4) {
      if (!form.acesso.nivel) return toast.error("Informe como é o acesso ao local");
      if (!form.acesso.caminhaoPara) return toast.error("Informe se o caminhão consegue parar próximo ao imóvel");
      if (form.acesso.caminhaoPara === "nao" && !form.acesso.distancia) return toast.error("Informe a distância aproximada do caminhão até o imóvel");
    }
    if (step === 5) {
      if (form.escada.existe === null) return toast.error("Informe se existe escada");
      if (form.escada.existe && (!form.escada.degraus || form.escada.degraus < 1)) return toast.error("Informe a quantidade total de degraus da escada");
      if (form.elevador.existe === null) return toast.error("Informe se existe elevador");
      if (form.elevador.existe && !form.elevador.comporta) return toast.error("Informe se o elevador comporta os objetos");
    }
    if (step === 6 && form.ajudantes.precisa === null) return toast.error("Informe se precisa de ajudante");
    if (step === 7 && form.carona === null) return toast.error("Escolha se deseja carona com o motorista");
    if (step === 10) {
      if (!form.cliente.nome.trim()) return toast.error("Informe seu nome");
      if (form.cliente.telefone.replace(/\D/g, "").length < 10) return toast.error("Informe um telefone/WhatsApp válido");
      if (!form.agendamento.data) return toast.error("Informe a data desejada do serviço");
      if (!form.agendamento.horario) return toast.error("Informe o horário desejado do serviço");
      const todayStr = new Date().toISOString().split("T")[0];
      if (form.agendamento.data < todayStr || form.agendamento.data > maxDate) {
        return toast.error("Essa data está fora do período disponível para agendamento. Escolha uma data dentro dos próximos 3 meses.");
      }
      if (occupiedDates.includes(form.agendamento.data)) return toast.error("Esta data já está reservada. Escolha outra data.");
      return submit();
    }
    // Etapa 3 (Serviços adicionais): só existe quando o ADM habilitou algo
    if (step === 2 && servicosCfg && !servicosCfg.disponivel) {
      setStep(4);
      window.scrollTo({ top: 0 });
      return;
    }
    if (step === 3) {
      const sel = form.servicosAdicionais || { itens: {}, pacote_id: "" };
      const pac = (servicosCfg?.pacotes || []).find((p) => p.id === sel.pacote_id);
      const srvPacote = new Set(pac?.servicos || []);
      const qtdSrv = (k) => Object.values(sel.itens?.[k] || {}).reduce((a, b) => a + b, 0);
      const envolveMontDesmont =
        srvPacote.has("montagem") || srvPacote.has("desmontagem") || qtdSrv("montagem") > 0 || qtdSrv("desmontagem") > 0;
      if (envolveMontDesmont && (form.servicosFotos || []).length === 0) {
        return toast.error("Envie ao menos uma foto dos itens que serão montados ou desmontados");
      }
    }
    // Etapa 8 (Serviços sob consulta): oculta serviços com preço OU desabilitados pelo ADM; pula quando não sobra nenhum
    const sobConsultaVisiveis = ["embalagem", "desmontagem", "montagem"].filter((k) => !servicosCfg?.servicos?.[k] && !(servicosCfg?.desabilitados || []).includes(k));
    if (step === 7 && sobConsultaVisiveis.length === 0) {
      setStep(9);
      window.scrollTo({ top: 0 });
      return;
    }
    setStep((s) => s + 1);
    window.scrollTo({ top: 0 });
  };

  const back = () => {
    if (step === 0) return navigate("/");
    const sobConsultaVisiveis = ["embalagem", "desmontagem", "montagem"].filter((k) => !servicosCfg?.servicos?.[k] && !(servicosCfg?.desabilitados || []).includes(k));
    if (step === 9 && sobConsultaVisiveis.length === 0) {
      setStep(7);
      window.scrollTo({ top: 0 });
      return;
    }
    if (step === 4 && servicosCfg && !servicosCfg.disponivel) {
      setStep(2);
      window.scrollTo({ top: 0 });
      return;
    }
    setStep((s) => s - 1);
    window.scrollTo({ top: 0 });
  };

  const addStop = () => set("stops", [...form.stops, emptyAddr()]);
  const removeStop = (i) => {
    set("stops", form.stops.filter((_, idx) => idx !== i));
    setRouteInfo(null);
  };
  const setStop = (i, v) => {
    const next = [...form.stops];
    next[i] = v;
    set("stops", next);
    setRouteInfo(null);
  };

  const uploadPhotos = async (fileList) => {
    const files = [...fileList];
    if (!files.length) return;
    setUploading(true);
    try {
      const otimizadas = [];
      for (const f of files) {
        otimizadas.push(await processarFoto(f));
      }
      const fd = new FormData();
      otimizadas.forEach((f) => fd.append("files", f));
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newPreviews = r.data.files.map((f, i) => ({
        path: f.path,
        preview: URL.createObjectURL(otimizadas[i]),
      }));
      setPhotoPreviews((p) => [...p, ...newPreviews]);
      setForm((f) => ({ ...f, fotos: [...f.fotos, ...r.data.files.map((x) => x.path)] }));
      toast.success("Fotos enviadas!");
    } catch (e) {
      files.forEach((f) => logErroUpload("orcamento", e, f));
      toast.error(e.response?.data?.detail || "Não foi possível enviar a foto. Tente novamente.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        tipo: form.tipo,
        cliente: form.cliente,
        origin: form.origin,
        destination: form.destination,
        stops: form.stops,
        items: form.items,
        outros_itens: form.outrosItens,
        acesso: form.acesso,
        escada: form.escada,
        elevador: form.elevador,
        ajudantes: form.ajudantes,
        carona: form.carona,
        servicos: form.servicos,
        servicos_adicionais: form.servicosAdicionais || { itens: {}, pacote_id: "" },
        servicos_fotos: form.servicosFotos || [],
        fotos: form.fotos,
        agendamento: form.agendamento,
        urgente: !!form.urgente,
        ref_codigo: (localStorage.getItem("cjr_ref") || searchParams.get("ref") || "").toUpperCase(),
      };
      const t = localStorage.getItem("client_token");
      const cfg = t ? { headers: { Authorization: `Bearer ${t}` } } : {};
      const r = editQuoteId
        ? await api.put(`/cliente/orcamentos/${editQuoteId}`, payload, cfg)
        : await api.post("/quotes", payload);
      setResult(r.data);
      if (editQuoteId) {
        const aj = (r.data || {}).ajuste;
        if (aj) toast.warning(`Novo valor salvo. Diferença de ${brl(Math.abs(aj.diferenca))} em relação ao valor já pago — a empresa fará o acerto com você.`, { duration: 8000 });
        else toast.success("Orçamento atualizado com o novo valor!");
      }
      api.delete("/cliente/rascunho", cfg).catch(() => {});
      setStep(11);
      window.scrollTo({ top: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível calcular seu orçamento. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  const totalItems = Object.values(form.items).reduce((a, b) => a + b, 0);

  const maxDate = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 3);
    return d.toISOString().split("T")[0];
  })();

  const paymentPayload = () => ({
    quote_id: result?.quote_id,
    forma: payment.forma === "cartao" ? payment.cardTipo : payment.forma || "dinheiro",
    bandeira: payment.bandeira || null,
    parcelas: (payment.forma === "cartao" ? payment.cardTipo : payment.forma) === "credito" ? payment.parcelas : null,
    cpf_cnpj: payment.cpfCnpj,
  });

  const refreshReservation = async () => {
    if (!reservation) return;
    const r = await api.get(`/reservations/${reservation.reservation_id}`);
    setReservation((prev) => ({ ...prev, ...r.data }));
  };

  const reservaWhatsAppLink = (r) => {
    const num = publicSettings?.whatsapp_number || "5521986009971";
    const addr = (a) => [a.logradouro && a.numero ? `${a.logradouro}, ${a.numero}` : a.cep, a.cidade && a.uf ? `${a.cidade}-${a.uf}` : a.cidade].filter(Boolean).join(" ");
    const itemsTxt = Object.entries(form.items).map(([k, v]) => `${k} x${v}`).join(", ") + (form.outrosItens ? ` + outros: ${form.outrosItens}` : "");
    const dt = r.pix_informado_em ? new Date(r.pix_informado_em) : new Date();
    const lines = [
      "🚨 NOVO PAGAMENTO DE SINAL — AGUARDANDO CONFERÊNCIA",
      "",
      `*Cliente:* ${form.cliente.nome}`,
      `*Telefone:* ${form.cliente.telefone}`,
      `*Reserva:* ${r.numero_reserva}`,
      `*Contrato:* ${r.numero_contrato}`,
      `*Serviço:* ${form.tipo === "frete" ? "FRETE" : "MUDANÇA"}`,
      `*Origem:* ${addr(form.origin)}`,
      `*Destino:* ${addr(form.destination)}`,
      `*Data do serviço:* ${r.data.split("-").reverse().join("/")}`,
      `*Horário solicitado:* ${r.horario}`,
      `*Itens:* ${itemsTxt || "-"}`,
      `*Valor do serviço:* ${brl(r.total)}`,
      `*Sinal de 30% (PIX):* ${brl(r.sinal)}`,
      `*Saldo restante:* ${brl(r.saldo)}`,
      "*Forma de pagamento do sinal:* PIX",
      "*Cliente informou o pagamento:* SIM",
      `*Data da confirmação:* ${dt.toLocaleDateString("pt-BR")}`,
      `*Hora da confirmação:* ${dt.toLocaleTimeString("pt-BR")}`,
      `*Contrato (PDF):* ${API}/reservations/${r.reservation_id}/pdf`,
    ];
    return `https://wa.me/${num}?text=${encodeURIComponent(lines.join("\n"))}`;
  };

  const whatsappLink = () => {
    const num = publicSettings?.whatsapp_number || "5521986009971";
    const addr = (a) => [a.logradouro && a.numero ? `${a.logradouro}, ${a.numero}` : a.cep, a.cidade && a.uf ? `${a.cidade}-${a.uf}` : a.cidade].filter(Boolean).join(" ");
    const lines = [
      "Olá! Fiz uma solicitação de orçamento pelo aplicativo Fretes e Mudanças e gostaria de falar sobre meu serviço.",
      "",
      `*Nome:* ${form.cliente.nome}`,
      `*Serviço:* ${form.tipo === "frete" ? "FRETE" : "MUDANÇA"}`,
      `*Data desejada:* ${form.agendamento.data.split("-").reverse().join("/")}`,
      `*Horário desejado:* ${form.agendamento.horario}`,
      `*Origem:* ${addr(form.origin)}`,
      ...(form.stops.length ? [`*Paradas:* ${form.stops.length} (a combinar)`] : []),
      `*Destino:* ${addr(form.destination)}`,
      `*Distância:* ${result?.distance_km} km`,
      `*Itens:* ${totalItems}${form.outrosItens ? " + outros" : ""}`,
      `*Ajudantes:* ${form.ajudantes.precisa ? (form.ajudantes.quantidade > 4 ? "mais de 4" : form.ajudantes.quantidade) : "não"}`,
      `*Carona:* ${form.carona ? "Sim" : "Não"}`,
      `*Valor do orçamento:* ${brl(result?.total)}`,
    ];
    return `https://wa.me/${num}?text=${encodeURIComponent(lines.join("\n"))}`;
  };

  const identificado = Boolean(clientToken) || Boolean(googleUser);

  useEffect(() => {
    if (!identificado || editQuoteId || searchParams.get("editar") || searchParams.get("continuar")) return;
    if (restauradoRef.current) return;
    const cfg = clientToken ? { headers: { Authorization: `Bearer ${clientToken}` } } : {};
    api.get("/cliente/rascunho", cfg).then((r) => {
      const d = r.data?.rascunho;
      if (d && d.form) {
        setForm((f) => ({ ...f, ...d.form }));
        if (typeof d.step === "number" && d.step > 0) setStep(d.step);
        toast.info("Continuando seu orçamento de onde você parou.");
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identificado]);

  useEffect(() => {
    if (!identificado || editQuoteId || result) return;
    const t = setTimeout(() => {
      const cfg = clientToken ? { headers: { Authorization: `Bearer ${clientToken}` } } : {};
      api.put("/cliente/rascunho", { form, step }, cfg).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, step, identificado, editQuoteId, result]);

  if (!identificado) {
    return (
      <div className="min-h-screen bg-[#0b1220] flex justify-center">
        <div className="w-full max-w-md mx-auto min-h-screen bg-slate-50 shadow-2xl pb-10" data-testid="wizard-gate-screen">
          <Header />
          <ClienteGate onIdentified={(info) => {
            if (info.token) { localStorage.setItem("client_token", info.token); setClientToken(info.token); }
            setSub("cliente", { nome: info.nome || "", telefone: info.telefone || "", cpf: info.cpf || "", email: info.email || "" });
            if (searchParams.get("editar") || searchParams.get("continuar")) window.location.reload();
          }} />
        </div>
      </div>
    );
  }

  const nomeOk = !!form.cliente.nome.trim();
  const telOk = (form.cliente.telefone || "").replace(/\D/g, "").length >= 10;
  const mostrarDadosCliente = !nomeOk || !telOk;

  const progress = Math.round(((Math.min(step, TOTAL_STEPS - 1) + 1) / TOTAL_STEPS) * 100);

  return (
    <div className="min-h-screen bg-[#0b1220] flex justify-center">
      <div className="w-full max-w-md mx-auto min-h-screen bg-slate-50 shadow-2xl relative pb-28" data-testid="quote-wizard">
        <Header />
        <div className="px-4 pt-4">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs font-semibold tracking-wider uppercase text-slate-400" data-testid="wizard-step-label">
              Passo {Math.min(step + 1, TOTAL_STEPS)} de {TOTAL_STEPS} · {STEP_TITLES[step]}
            </p>
            {form.tipo && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-navy text-[#F5B301] px-2 py-0.5 rounded-full" data-testid="wizard-tipo-badge">
                {form.tipo === "frete" ? "Frete" : "Mudança"}
              </span>
            )}
          </div>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-[#F5B301] transition-all duration-500" style={{ width: `${progress}%` }} data-testid="wizard-progress" />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ x: 24, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -24, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="px-4 pt-5 space-y-5"
          >
            {step === 0 && (
              <div className="space-y-4">
                <h2 className="font-heading text-2xl font-bold text-navy">O que você precisa transportar?</h2>
                <button
                  data-testid="wizard-frete-option"
                  onClick={() => set("tipo", "frete")}
                  className={`w-full p-5 rounded-2xl border-2 text-left active:scale-[0.98] transition-all flex items-center gap-4 ${
                    form.tipo === "frete" ? "border-[#F5B301] bg-gold-light" : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="w-12 h-12 rounded-xl bg-navy flex items-center justify-center shrink-0">
                    <Truck className="w-6 h-6 text-[#F5B301]" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-navy">FRETE</p>
                    <p className="text-xs text-slate-500 mt-0.5">Para transporte de um item ou poucos itens.</p>
                  </div>
                </button>
                <button
                  data-testid="wizard-mudanca-option"
                  onClick={() => set("tipo", "mudanca")}
                  className={`w-full p-5 rounded-2xl border-2 text-left active:scale-[0.98] transition-all flex items-center gap-4 ${
                    form.tipo === "mudanca" ? "border-[#F5B301] bg-gold-light" : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="w-12 h-12 rounded-xl bg-navy flex items-center justify-center shrink-0">
                    <HomeIcon className="w-6 h-6 text-[#F5B301]" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-navy">MUDANÇA</p>
                    <p className="text-xs text-slate-500 mt-0.5">Para transporte de vários móveis, caixas e objetos.</p>
                  </div>
                </button>
                {publicSettings?.entrega_ativa && (
                  <button
                    data-testid="wizard-entrega-option"
                    onClick={() => set("tipo", "entrega")}
                    className={`w-full p-5 rounded-2xl border-2 text-left active:scale-[0.98] transition-all flex items-center gap-4 ${
                      form.tipo === "entrega" ? "border-[#F5B301] bg-gold-light" : "border-slate-200 bg-white"
                    }`}
                  >
                    <div className="w-12 h-12 rounded-xl bg-navy flex items-center justify-center shrink-0">
                      <Package className="w-6 h-6 text-[#F5B301]" />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-navy">ENTREGA</p>
                      <p className="text-xs text-slate-500 mt-0.5">Para entregas rápidas de pacotes e encomendas.</p>
                    </div>
                  </button>
                )}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5">
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
                  <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2 mb-3">
                    <Package className="w-4 h-4 text-[#F5B301]" /> LOCAL DE EMBARQUE
                  </h3>
                  <AddressForm value={form.origin} onChange={(v) => { set("origin", v); setRouteInfo(null); }} testIdPrefix="origin" />
                </div>

                {form.stops.map((stop, i) => (
                  <div key={i} className="bg-white rounded-2xl border-2 border-[#F5B301]/50 shadow-sm p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2">
                        <Flag className="w-4 h-4 text-[#F5B301]" /> PARADA {i + 1}
                      </h3>
                      <button data-testid={`remove-stop-btn-${i}`} onClick={() => removeStop(i)} className="p-1.5 text-red-400 hover:text-red-600">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <AddressForm value={stop} onChange={(v) => setStop(i, v)} testIdPrefix={`stop-${i}`} showObs />
                    <p className="text-[11px] text-amber-600 font-semibold mt-2">Parada adicional: a combinar com o motorista.</p>
                  </div>
                ))}

                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
                  <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2 mb-3">
                    <Flag className="w-4 h-4 text-navy" /> DESTINO FINAL
                  </h3>
                  <AddressForm value={form.destination} onChange={(v) => { set("destination", v); setRouteInfo(null); }} testIdPrefix="destination" />
                </div>

                <button
                  data-testid="add-stop-btn"
                  onClick={addStop}
                  className="w-full py-3.5 rounded-xl border-2 border-dashed border-[#F5B301] text-navy font-semibold text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform bg-gold-light/50"
                >
                  <Plus className="w-4 h-4" /> ADICIONAR PARADA
                </button>

                <RoutePreview origin={form.origin} stops={form.stops} destination={form.destination} onRemoveStop={removeStop} routeInfo={routeInfo} />
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="font-heading text-2xl font-bold text-navy mb-1">O que vamos transportar?</h2>
                <p className="text-sm text-slate-500 mb-4">Informe a quantidade de cada item.</p>
                {form.tipo === "frete" && (
                  <p data-testid="frete-limit-notice" className="text-xs bg-gold-light border border-[#F5B301]/60 text-navy rounded-xl px-3.5 py-2.5 mb-3 font-medium">
                    Frete: até 3 itens. Acima dessa quantidade, o serviço é classificado automaticamente como Mudança.
                  </p>
                )}
                <ItemSelector
                  items={form.items}
                  onChange={setItems}
                  outrosItens={form.outrosItens}
                  onOutrosChange={(v) => set("outrosItens", v)}
                />
              </div>
            )}

            {step === 3 && servicosCfg?.disponivel && (
              <ServicosAdicionaisStep
                cfg={servicosCfg}
                value={form.servicosAdicionais}
                onChange={(v) => set("servicosAdicionais", v)}
                fotos={form.servicosFotos}
                onFotosChange={(paths) => set("servicosFotos", paths)}
              />
            )}

            {step === 4 && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-heading font-bold text-navy text-sm mb-2.5">COMO É O ACESSO AO LOCAL?</h3>
                  <OptionGrid
                    options={[
                      { value: "facil", label: "Fácil" },
                      { value: "moderado", label: "Moderado" },
                      { value: "dificil", label: "Difícil" },
                    ]}
                    value={form.acesso.nivel}
                    onChange={(v) => setSub("acesso", { nivel: v })}
                    testIdPrefix="acesso"
                  />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-navy text-sm mb-2.5">O CAMINHÃO CONSEGUE PARAR PRÓXIMO AO IMÓVEL?</h3>
                  <OptionGrid
                    options={[
                      { value: "sim", label: "Sim" },
                      { value: "parcialmente", label: "Parcialmente" },
                      { value: "nao", label: "Não" },
                    ]}
                    value={form.acesso.caminhaoPara}
                    onChange={(v) => setSub("acesso", { caminhaoPara: v, distancia: v === "nao" ? form.acesso.distancia : "" })}
                    testIdPrefix="caminhao"
                  />
                </div>
                {form.acesso.caminhaoPara === "nao" && (
                  <div>
                    <h3 className="font-heading font-bold text-navy text-sm mb-2.5">DISTÂNCIA APROXIMADA DO CAMINHÃO ATÉ O IMÓVEL</h3>
                    <OptionGrid
                      options={[
                        { value: "ate10", label: "até 10 m" },
                        { value: "10-30", label: "10–30 m" },
                        { value: "30-50", label: "30–50 m" },
                        { value: "50+", label: "mais de 50 m" },
                      ]}
                      value={form.acesso.distancia}
                      onChange={(v) => setSub("acesso", { distancia: v })}
                      testIdPrefix="distancia"
                    />
                  </div>
                )}
              </div>
            )}

            {step === 5 && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-heading font-bold text-navy text-sm mb-2.5">EXISTE ESCADA?</h3>
                  <OptionGrid
                    options={[{ value: "nao", label: "Não" }, { value: "sim", label: "Sim" }]}
                    value={form.escada.existe === null ? "" : form.escada.existe ? "sim" : "nao"}
                    onChange={(v) => setSub("escada", { existe: v === "sim" })}
                    testIdPrefix="escada"
                  />
                </div>
                {form.escada.existe && (
                  <div>
                    <h3 className="font-heading font-bold text-navy text-sm mb-2.5">QUANTOS DEGRAUS NO TOTAL?</h3>
                    <input
                      data-testid="escada-degraus"
                      type="number"
                      inputMode="numeric"
                      min="1"
                      max="300"
                      className="w-full px-4 py-3.5 rounded-xl border-2 border-slate-200 focus:border-[#F5B301] focus:outline-none text-sm"
                      placeholder="Ex.: 18"
                      value={form.escada.degraus || ""}
                      onChange={(e) => {
                        const n = parseInt(e.target.value) || 0;
                        setSub("escada", { degraus: n, lances: Math.max(1, Math.ceil(n / 20)) });
                      }}
                    />
                    <p className="text-[11px] text-slate-400 mt-1.5">Some os degraus de todos os lances do local (embarque e/ou desembarque).</p>
                  </div>
                )}
                <div>
                  <h3 className="font-heading font-bold text-navy text-sm mb-2.5">EXISTE ELEVADOR?</h3>
                  <OptionGrid
                    options={[{ value: "nao", label: "Não" }, { value: "sim", label: "Sim" }]}
                    value={form.elevador.existe === null ? "" : form.elevador.existe ? "sim" : "nao"}
                    onChange={(v) => setSub("elevador", { existe: v === "sim", comporta: v === "sim" ? form.elevador.comporta : "" })}
                    testIdPrefix="elevador"
                  />
                </div>
                {form.elevador.existe && (
                  <div>
                    <h3 className="font-heading font-bold text-navy text-sm mb-2.5">O ELEVADOR COMPORTA OS OBJETOS?</h3>
                    <OptionGrid
                      options={[
                        { value: "sim", label: "Sim" },
                        { value: "nao", label: "Não" },
                        { value: "nao_sei", label: "Não sei" },
                      ]}
                      value={form.elevador.comporta}
                      onChange={(v) => setSub("elevador", { comporta: v })}
                      testIdPrefix="elevador-comporta"
                    />
                  </div>
                )}
              </div>
            )}

            {step === 6 && (
              <div className="space-y-5">
                <h2 className="font-heading text-2xl font-bold text-navy">Precisa de ajudante?</h2>
                <OptionGrid
                  options={[
                    { value: "nao", label: "NÃO PRECISO DE AJUDANTE" },
                    { value: "sim", label: "SIM, PRECISO DE AJUDANTE" },
                  ]}
                  value={form.ajudantes.precisa === null ? "" : form.ajudantes.precisa ? "sim" : "nao"}
                  onChange={(v) => setSub("ajudantes", { precisa: v === "sim" })}
                  testIdPrefix="ajudantes"
                />
                {form.ajudantes.precisa && (
                  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex items-center justify-between">
                    <div>
                      <p className="font-heading font-bold text-navy text-sm">QUANTIDADE DE AJUDANTES</p>
                      {form.ajudantes.quantidade > 4 && (
                        <p className="text-[11px] text-amber-600 font-semibold mt-1">Mais de 4: avaliação manual</p>
                      )}
                    </div>
                    <Counter
                      value={form.ajudantes.quantidade}
                      onChange={(v) => setSub("ajudantes", { quantidade: v })}
                      min={1}
                      max={5}
                      plusLabel="4+"
                      testId="ajudantes-counter"
                    />
                  </div>
                )}
              </div>
            )}

            {step === 7 && <CaronaCard value={form.carona} onChange={(v) => set("carona", v)} />}

            {step === 8 && (
              <div className="space-y-4">
                <h2 className="font-heading text-2xl font-bold text-navy">Serviços adicionais</h2>
                {[
                  { key: "embalagem", label: "Precisa de embalagem?" },
                  { key: "desmontagem", label: "Precisa de desmontagem?" },
                  { key: "montagem", label: "Precisa de montagem?" },
                ]
                  .filter((s) => !servicosCfg?.servicos?.[s.key] && !(servicosCfg?.desabilitados || []).includes(s.key))
                  .map((s) => (
                  <div key={s.key} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid={`servico-${s.key}`}>
                    <p className="font-heading font-bold text-navy text-sm mb-2.5 uppercase">{s.label}</p>
                    <OptionGrid
                      options={[{ value: "nao", label: "Não" }, { value: "sim", label: "Sim" }]}
                      value={form.servicos[s.key] ? "sim" : "nao"}
                      onChange={(v) => setForm((f) => ({ ...f, servicos: { ...f.servicos, [s.key]: v === "sim" } }))}
                      testIdPrefix={`servico-${s.key}`}
                    />
                    {form.servicos[s.key] && (
                      <p className="text-[11px] text-amber-600 font-semibold mt-2 uppercase tracking-wide">
                        {s.key} — sob consulta
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {step === 9 && (
              <div className="space-y-4">
                <h2 className="font-heading text-2xl font-bold text-navy">Fotos</h2>
                <p className="text-sm text-slate-500">
                  Envie fotos de móveis, objetos, escadas, corredores, acessos, estacionamento e itens especiais. (Opcional)
                </p>
                <label
                  data-testid="fotos-upload-label"
                  className="flex flex-col items-center justify-center gap-2 w-full py-10 rounded-2xl border-2 border-dashed border-slate-300 bg-white text-slate-500 cursor-pointer active:scale-[0.98] transition-transform"
                >
                  {uploading ? <Loader2 className="w-8 h-8 animate-spin text-[#F5B301]" /> : <Camera className="w-8 h-8 text-navy" />}
                  <span className="text-sm font-semibold">{uploading ? "Enviando..." : "Toque para enviar fotos"}</span>
                  <input
                    data-testid="fotos-upload-input"
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => uploadPhotos(e.target.files)}
                  />
                </label>
                {photoPreviews.length > 0 && (
                  <div className="grid grid-cols-3 gap-2" data-testid="fotos-preview-grid">
                    {photoPreviews.map((p, i) => (
                      p.preview ? (
                        <img key={i} src={p.preview} alt="" className="w-full h-24 object-cover rounded-xl border border-slate-200" />
                      ) : (
                        <div key={i} className="w-full h-24 rounded-xl border border-emerald-200 bg-emerald-50 flex flex-col items-center justify-center gap-1">
                          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                          <span className="text-[9px] font-bold text-emerald-700">Foto enviada</span>
                        </div>
                      )
                    ))}
                  </div>
                )}
              </div>
            )}

            {step === 10 && (
              <div className="space-y-4">
                <h2 className="font-heading text-2xl font-bold text-navy">Quase lá!</h2>
                <p className="text-sm text-slate-500">{mostrarDadosCliente ? "Complete seus dados e escolha a data do serviço." : "Seus dados já estão salvos na sua conta — escolha a data e o horário do serviço."}</p>
                {clientToken ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="wizard-identificado-chip">
                    <p className="text-xs text-emerald-700 font-semibold flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4 shrink-0" /> Identificado como <strong>{form.cliente.nome}</strong> — dados vinculados à sua conta
                    </p>
                  </div>
                ) : googleUser ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center justify-between" data-testid="wizard-google-user">
                    <p className="text-xs text-emerald-700 font-semibold flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4 shrink-0" /> Conectado como <strong>{googleUser.nome}</strong>
                    </p>
                    <button data-testid="wizard-google-logout" onClick={() => { api.post("/auth/logout").catch(() => {}); setGoogleUser(null); }}
                      className="text-[11px] text-slate-400 underline shrink-0">sair</button>
                  </div>
                ) : (
                  <button data-testid="wizard-google-login-btn" onClick={() => {
                      try { sessionStorage.setItem("cjr_wizard_state", JSON.stringify({ form, step, payment, ts: Date.now() })); } catch {}
                      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                      const redirectUrl = window.location.origin + "/orcamento";
                      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                    }}
                    className="w-full py-3.5 rounded-2xl bg-white border-2 border-slate-200 text-navy font-heading font-bold text-xs flex items-center justify-center gap-2 active:scale-95 transition-transform">
                    <Chrome className="w-4 h-4" /> ENTRAR COM GOOGLE — PREENCHER AUTOMATICAMENTE
                  </button>
                )}
                {mostrarDadosCliente && (<>
                {!nomeOk && (
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Nome *</label>
                  <input
                    data-testid="cliente-nome-input"
                    className={`${inputCls} mt-1`}
                    placeholder="Seu nome"
                    value={form.cliente.nome}
                    onChange={(e) => setSub("cliente", { nome: e.target.value })}
                  />
                </div>
                )}
                {!telOk && (
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Telefone / WhatsApp *</label>
                  <input
                    data-testid="cliente-telefone-input"
                    inputMode="tel"
                    className={`${inputCls} mt-1`}
                    placeholder="(21) 99999-9999"
                    value={form.cliente.telefone}
                    onChange={(e) => setSub("cliente", { telefone: e.target.value })}
                  />
                </div>
                )}
                {!form.cliente.cpf && (
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">CPF (opcional)</label>
                  <input
                    data-testid="cliente-cpf-input"
                    inputMode="numeric"
                    className={`${inputCls} mt-1`}
                    placeholder="000.000.000-00"
                    value={form.cliente.cpf}
                    onChange={(e) => setSub("cliente", { cpf: e.target.value })}
                  />
                  <p className="text-[10px] text-slate-400 mt-1">Informe para consultar e alterar seus orçamentos depois na área do cliente (telefone + CPF).</p>
                </div>
                )}
                {!form.cliente.email && (
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">E-mail</label>
                  <input
                    data-testid="cliente-email-input"
                    type="email"
                    className={`${inputCls} mt-1`}
                    placeholder="seu@email.com"
                    value={form.cliente.email}
                    onChange={(e) => setSub("cliente", { email: e.target.value })}
                  />
                </div>
                )}
                </>)}
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Data desejada *</label>
                  <div className="mt-1 bg-white rounded-xl border border-slate-200 p-1 flex justify-center" data-testid="cliente-data-calendar">
                    <Calendar
                      mode="single"
                      showOutsideDays={false}
                      selected={form.agendamento.data ? new Date(form.agendamento.data + "T12:00:00") : undefined}
                      onSelect={(d) =>
                        setSub("agendamento", {
                          data: d
                            ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
                            : "",
                        })
                      }
                      disabled={[
                        { before: new Date(new Date().setHours(0, 0, 0, 0)) },
                        { after: new Date(maxDate + "T23:59:59") },
                        ...occupiedDates.map((ds) => new Date(ds + "T12:00:00")),
                      ]}
                      fromDate={new Date()}
                      toDate={new Date(maxDate + "T12:00:00")}
                    />
                  </div>
                  {form.agendamento.data && (
                    <p className="text-xs text-navy font-semibold mt-1.5" data-testid="data-selecionada">
                      Data selecionada: {form.agendamento.data.split("-").reverse().join("/")}
                    </p>
                  )}
                  <p className="text-[11px] text-slate-400 mt-1">
                    Somente datas dentro dos próximos 3 meses podem ser selecionadas. Datas ocupadas ficam bloqueadas.
                  </p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Horário desejado *</label>
                  <input
                    data-testid="cliente-horario-input"
                    type="time"
                    className={`${inputCls} mt-1`}
                    value={form.agendamento.horario}
                    onChange={(e) => setSub("agendamento", { horario: e.target.value })}
                  />
                </div>
                <p data-testid="agendamento-info" className="text-[11px] text-slate-500 leading-relaxed bg-slate-100 rounded-xl p-3">
                  A data e o horário informados são uma preferência do cliente e poderão ser ajustados no fechamento do serviço, de acordo com a disponibilidade e confirmação do profissional.
                </p>
                {publicSettings?.urgencia?.ativo && (
                  <label data-testid="wizard-urgencia-toggle" className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-3.5 cursor-pointer">
                    <input type="checkbox" className="w-5 h-5 accent-red-600" checked={!!form.urgente}
                      onChange={(e) => setForm({ ...form, urgente: e.target.checked })} />
                    <span className="text-xs font-bold text-red-700">🚨 Preciso com urgência (+{publicSettings.urgencia.percentual}% sobre o valor)</span>
                  </label>
                )}
              </div>
            )}

            {step === 11 && result && (
              <div className="space-y-4" data-testid="quote-result">
                <div className="text-center">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                  <h2 className="font-heading text-2xl font-extrabold text-navy">SEU ORÇAMENTO</h2>
                </div>
                <div className="bg-white rounded-3xl border border-slate-100 shadow-md p-5 space-y-3">
                  <div className="flex justify-between text-sm" data-testid="result-distance">
                    <span className="text-slate-500">Distância estimada</span>
                    <span className="font-semibold text-navy">{result.distance_km} km · ~{result.duration_min} min</span>
                  </div>
                  <div className="flex justify-between text-sm" data-testid="result-transport">
                    <span className="text-slate-500">Valor do transporte</span>
                    <span className="font-semibold text-navy">{brl(result.transport_value)}</span>
                  </div>
                  {result.promocao && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3" data-testid="result-promocao">
                      <p className="text-[11px] font-extrabold text-red-600">🔥 {result.promocao.nome}</p>
                      <div className="flex justify-between text-xs mt-1">
                        <span className="text-slate-500">Valor normal</span>
                        <span className="line-through text-slate-400">{brl(result.valor_normal)}</span>
                      </div>
                      <div className="flex justify-between text-xs mt-0.5">
                        <span className="text-slate-500">Desconto{result.promocao.percentual ? ` (${result.promocao.percentual}%)` : ""}</span>
                        <span className="font-bold text-emerald-600">-{brl(result.desconto)}</span>
                      </div>
                    </div>
                  )}
                  {result.urgente && (
                    <div className="flex justify-between text-sm" data-testid="result-urgente">
                      <span className="text-red-600 font-semibold">🚨 Urgência</span>
                      <span className="text-[10px] font-bold text-red-500 self-center">INCLUSA NO VALOR</span>
                    </div>
                  )}
                  {result.toll !== null && result.toll > 0 && (
                    <div className="flex justify-between text-sm" data-testid="result-toll">
                      <span className="text-slate-500">Pedágio</span>
                      <span className="font-semibold text-navy">{brl(result.toll)}</span>
                    </div>
                  )}
                  {result.helpers_qty > 0 && (
                    <div className="flex justify-between text-sm" data-testid="result-helpers">
                      <span className="text-slate-500">Ajudantes</span>
                      <span className="font-semibold text-navy">{result.helpers_qty > 4 ? "mais de 4" : result.helpers_qty}</span>
                    </div>
                  )}
                  {result.carona && (
                    <div className="flex justify-between text-sm" data-testid="result-carona">
                      <span className="text-slate-500">Carona com o motorista</span>
                      <span className="font-semibold text-navy">incluída no orçamento</span>
                    </div>
                  )}
                  {result.stops_count > 0 && (
                    <div className="flex justify-between text-sm" data-testid="result-stops">
                      <span className="text-slate-500">{result.stops_count === 1 ? "Parada" : `${result.stops_count} paradas`}</span>
                      <span className="font-semibold text-amber-600">a combinar com o motorista</span>
                    </div>
                  )}
                  {result.services_consulta.map((s) => (
                    <div key={s} className="flex justify-between text-sm" data-testid={`result-service-${s}`}>
                      <span className="text-slate-500 capitalize">{s}</span>
                      <span className="font-semibold text-amber-600">sob consulta</span>
                    </div>
                  ))}
                  {(result.servicos_adicionais || []).map((s, i) => (
                    <div key={`sa-${i}`} className="flex justify-between text-sm" data-testid={`result-serv-add-${i}`}>
                      <span className="text-slate-500">
                        {s.tipo === "pacote" ? `📦 ${s.nome}` : `${s.nome} (${s.tipo})${s.qtd > 1 ? ` x${s.qtd}` : ""}`}
                      </span>
                      <span className="font-semibold text-navy">{brl(s.valor)}</span>
                    </div>
                  ))}
                  <div className="border-t border-slate-100 pt-3 flex justify-between items-center" data-testid="result-total">
                    <span className="font-heading font-bold text-navy">TOTAL</span>
                    <span className="font-heading text-2xl font-extrabold text-navy">{brl(result.total)}</span>
                  </div>
                </div>

                {result.manual_evaluation && (
                  <div className="flex gap-2.5 bg-amber-50 border border-amber-200 rounded-2xl p-3.5" data-testid="result-manual-eval">
                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
                    <p className="text-xs text-amber-700 leading-relaxed">
                      Seu serviço tem características especiais. O valor final pode sofrer ajuste após avaliação — fale conosco para confirmar.
                    </p>
                  </div>
                )}

                <button
                  data-testid="whatsapp-speak-btn"
                  onClick={() => { setStep(12); window.scrollTo({ top: 0 }); }}
                  className="flex items-center justify-center gap-2 w-full py-4 rounded-2xl bg-[#25D366] text-white font-heading font-extrabold text-sm active:scale-95 transition-transform shadow-[0_8px_30px_rgba(37,211,102,0.35)]"
                >
                  <MessageCircle className="w-5 h-5" />
                  {form.tipo === "frete" ? "CONFIRME AQUI SEU FRETE" : "CONFIRME AQUI SUA MUDANÇA"}
                </button>
                <Link to="/" data-testid="new-quote-btn" className="block text-center text-sm text-slate-500 underline">
                  Fazer novo orçamento
                </Link>
              </div>
            )}
            {step === 12 && result && (
              <PaymentStep
                result={result}
                payment={payment}
                setPayment={setPayment}
                onBack={() => setStep(11)}
                onNext={() => { setStep(13); window.scrollTo({ top: 0 }); }}
              />
            )}

            {step === 13 && result && (
              <ContractStep
                payload={paymentPayload()}
                onBack={() => setStep(12)}
                onAccept={() => { setStep(14); window.scrollTo({ top: 0 }); }}
              />
            )}

            {step === 14 && result && payment.calc && (
              <SinalStep
                payload={paymentPayload()}
                calc={payment.calc}
                onBack={() => setStep(13)}
                onConfirmed={(res) => {
                  setReservation(res);
                  setStep(15);
                  window.scrollTo({ top: 0 });
                }}
              />
            )}

            {step === 15 && reservation && (
              <FinalStep reservation={reservation} whatsappLink={reservaWhatsAppLink(reservation)} onRefresh={refreshReservation} />
            )}
          </motion.div>
        </AnimatePresence>

        {step < 11 && (
          <div className="fixed bottom-0 left-0 right-0 z-40 flex justify-center">
            <div className="w-full max-w-md bg-white/95 backdrop-blur-md border-t border-slate-100 p-4 flex gap-3">
              <button
                data-testid="wizard-back-btn"
                onClick={back}
                className="px-5 py-3.5 rounded-xl border-2 border-slate-200 text-navy font-semibold text-sm flex items-center gap-1.5 active:scale-95 transition-transform"
              >
                <ArrowLeft className="w-4 h-4" /> Voltar
              </button>
              <button
                data-testid="wizard-next-btn"
                onClick={next}
                disabled={calcRoute || submitting}
                className="flex-1 py-3.5 rounded-xl bg-navy text-[#F5B301] font-heading font-extrabold text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
              >
                {calcRoute || submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {step === 1 ? "Calculando rota..." : "Calculando..."}
                  </>
                ) : step === 10 ? (
                  editQuoteId ? "SALVAR E RECALCULAR" : "CALCULAR MEU ORÇAMENTO"
                ) : (
                  <>
                    Continuar <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
